import {
  BrainCircuit,
  FileScan,
  Palette,
  Presentation,
  Wrench,
  type LucideIcon,
} from "lucide-react";

export type SystemNavCategory = {
  key: string;
  href: string;
  children?: readonly { key: string; href: string }[];
};

export type SystemSettingsZone = {
  key: "models" | "documents" | "tools" | "memory" | "playground" | "account";
  labelKey: string;
  itemCount: number;
  icon: LucideIcon;
  categoryKeys: readonly string[];
  shortcut?: { href: string; labelKey: string };
};

/**
 * The mock's six visual zones deliberately live beside, not inside,
 * settings-nav.ts. Category ownership and every existing route remain owned by
 * the shared settings navigation; this module only groups that data for the
 * system overview.
 */
export const SYSTEM_SETTINGS_ZONES: readonly SystemSettingsZone[] = [
  {
    key: "models",
    labelKey: "Models · Engines",
    itemCount: 6,
    icon: BrainCircuit,
    categoryKeys: ["models"],
  },
  {
    key: "documents",
    labelKey: "Documents · Indexing",
    itemCount: 4,
    icon: FileScan,
    categoryKeys: ["knowledge"],
  },
  {
    key: "tools",
    labelKey: "Tools · Capabilities",
    itemCount: 4,
    icon: Wrench,
    categoryKeys: ["chat", "agents"],
  },
  {
    key: "memory",
    labelKey: "Memory · Organization",
    itemCount: 3,
    icon: BrainCircuit,
    categoryKeys: ["memory"],
  },
  {
    key: "playground",
    labelKey: "Playground",
    itemCount: 2,
    icon: Presentation,
    categoryKeys: [],
    shortcut: { href: "/playground", labelKey: "Playground" },
  },
  {
    key: "account",
    labelKey: "Account · Appearance",
    itemCount: 4,
    icon: Palette,
    categoryKeys: ["appearance", "network"],
    shortcut: { href: "/profile", labelKey: "Profile" },
  },
];

export function categoriesForSystemZone<T extends SystemNavCategory>(
  zone: SystemSettingsZone,
  categories: readonly T[],
): T[] {
  return zone.categoryKeys.flatMap((categoryKey) => {
    const category = categories.find((item) => item.key === categoryKey);
    return category ? [category] : [];
  });
}

export function settingsHubState({
  settingsLoading,
  settingsError,
  catalogEditable,
}: {
  settingsLoading: boolean;
  settingsError: string | null;
  catalogEditable: boolean | null;
}): "loading" | "error" | "forbidden" | "ready" {
  if (settingsLoading) return "loading";
  if (settingsError) return "error";
  if (catalogEditable === false) return "forbidden";
  return "ready";
}
